/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React, { useEffect, useState, createContext } from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { focusHandling } from "cruip-js-toolkit"
import { useLocation } from "@reach/router"

import AOS from "aos"
import "instantsearch.css/themes/satellite.css"
import "/src/css/style.scss"
import "../i18n"

import Header from "./Header"
import Footer from "../partials/Footer"
import { graphql, useStaticQuery } from "gatsby"
import SearchBoxFilm from "./Search/SearchBoxFilm"
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache"
import { useTranslation } from "react-i18next"
import { defaultLanguage, languageCodes } from "../i18n/config"
import { useLanguageRedirect } from "../i18n/useLanguageRedirect"
import HrefLangTags from "./HrefLangTags"
import { isLegalPath, isEnOnlyPath } from "../utils/enOnlyPaths"
import { getCookie } from "@filmustage/shared"

/*export const CheckLighthouse = () => {
  if (typeof window !== "undefined")
    return navigator.userAgent.includes("Chrome-Lighthouse")
}*/
export const ChildContext = createContext(null)
const sessionStorageCache = createInfiniteHitsSessionStorageCache()

const Layout = ({ children, pageContext }) => {
  // https://www.youtube.com/watch?v=vNDbsdL0vvE
  const data = useStaticQuery(graphql`
    query GhostSettings {
      allGhostSettings {
        edges {
          node {
            ...GhostSettingsFields
          }
        }
      }
    }
  `)
  const settings = data.allGhostSettings.edges[0].node
  const location = useLocation()
  const [showSearchBox, setShowSearchBox] = useState(false)
  const [showEnOnlyNotice, setShowEnOnlyNotice] = useState(false)
  const { i18n, t } = useTranslation()

  // Detect language: use stored preference on blog/legal pages (EN-only content),
  // otherwise use pageContext.lang from Gatsby
  const isLegalPage = isLegalPath(location.pathname)
  const isEnOnlyPage = isEnOnlyPath(location.pathname)
  const storedLang =
    typeof window !== "undefined" ? getCookie("i18nextLng") : null
  const lang =
    isEnOnlyPage && storedLang
      ? storedLang
      : pageContext?.lang || defaultLanguage

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect browser language on first visit and redirect
  useLanguageRedirect()

  // Restore user's chosen language when landing on EN page
  // (e.g. direct link, bookmark) while having a stored preference
  useEffect(() => {
    if (typeof window === "undefined") return
    const stored = getCookie("i18nextLng")
    if (!stored || stored === defaultLanguage) return

    const pathname = window.location.pathname
    // Already on a lang-prefixed page - all good
    const firstSegment = pathname.split("/").filter(Boolean)[0]
    if (firstSegment && languageCodes.includes(firstSegment)) return
    // Blog and legal pages are EN-only, don't redirect
    if (isEnOnlyPath(pathname)) return

    // Redirect to stored language version
    window.location.href = `/${stored}${pathname}`
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Show EN-only notice on blog and legal pages
  useEffect(() => {
    const stored = getCookie("i18nextLng")
    setShowEnOnlyNotice(isEnOnlyPage && !!stored && stored !== defaultLanguage)
  }, [isEnOnlyPage, location.pathname])

  useEffect(() => {
    focusHandling("outline")
  }, [location])

  // AOS Gatsby js fix:
  // https://stackoverflow.com/questions/42569360/gatsbyjs-reactjs-aosanimate-on-scroll-causing-build-problems
  // https://github.com/floroz/portfolio-v1
  useEffect(() => {
    AOS.init({
      once: true,
      disable: false,
      duration: 600,
      easing: "ease-out-sine",
    })
  }, [])

  return (
    <ChildContext.Provider
      value={{
        showSearchBox,
        setShowSearchBox,
        sessionStorageCache,
      }}
    >
      <Helmet>
        <html lang={lang} />
        <body className="font-inter antialiased bg-gray-900 text-gray-200 tracking-tight" />
        <style type="text/css">{`${settings.codeinjection_styles}`}</style>
      </Helmet>
      <HrefLangTags
        path={(() => {
          const p = location.pathname
          const parts = p.split("/").filter(Boolean)
          if (parts.length > 0 && languageCodes.includes(parts[0])) {
            const rest = parts.slice(1).join("/")
            return rest ? `/${rest}/` : "/"
          }
          return p
        })()}
        availableLocales={isEnOnlyPage ? [defaultLanguage] : undefined}
      />

      <div className="flex flex-col min-h-screen overflow-x-clip">
        <Header />
        <SearchBoxFilm />
        {showEnOnlyNotice && (
          <div className="bg-indigo-900 border-b border-indigo-700 text-center py-2.5 text-sm text-indigo-300 mt-12 sm:mt-14">
            {isLegalPage ? t("legalNotice") : t("blogNotice")}
          </div>
        )}
        {children}
        <Footer />
      </div>
    </ChildContext.Provider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
